import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

export interface BulkProgressModalProps {
  open: boolean;
  title: string;
  status: string;
  detail?: string;
  value: number;
}

const BulkProgressModal: React.FC<BulkProgressModalProps> = ({
  open,
  title,
  status,
  detail,
  value,
}) => {
  const progressPercent = Math.min(100, Math.max(0, Math.round(value)));

  return (
    <Dialog
      data-testid="bulk-operation-progress-dialog"
      open={open}
      disableEscapeKeyDown
      BackdropProps={{ onClick: (e: React.MouseEvent) => e.preventDefault() }}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      PaperProps={{
        sx: (theme) => ({
          zIndex: theme.zIndex.modal + 10,
          borderRadius: '16px',
          background: theme.palette.white?.main || theme.palette.background.paper,
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            display: 'block',
            background: `linear-gradient(268.67deg, ${
              theme.palette.themeColorSecondary?.main || '#00839A'
            } -16.55%, ${theme.palette.themeColor?.main || '#00A8C5'} 73.37%)`,
            height: '16px',
            borderRadius: '16px 16px 0px 0px',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
          },
          '&:focus': {
            outline: '0 !important',
          },
          [theme.breakpoints.up('lg')]: {
            minWidth: '521px !important',
          },
          [theme.breakpoints.up('sm')]: {
            minWidth: '465px !important',
            maxWidth: '500px !important',
          },
          '@media screen and (max-width:600px)': {
            minWidth: '95% !important',
          },
        }),
      }}
    >
      <DialogTitle sx={{ padding: '24px 24px 8px 24px' }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: 1.3,
            color: '#000000',
            pr: 0,
          }}
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pb: 3,
          pt: '16px !important',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 1 }}
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${title} progress`}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={88}
              thickness={4}
              aria-hidden
              sx={{
                color: (theme) => theme.palette.grey[300],
              }}
            />
            <CircularProgress
              variant="determinate"
              value={progressPercent}
              size={88}
              thickness={4}
              sx={{
                position: 'absolute',
                left: 0,
                color: (theme) => theme.palette.themeColor?.main || theme.palette.primary.main,
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h6"
                component="div"
                aria-live="polite"
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '20px',
                  color: '#000000',
                }}
              >
                {`${progressPercent}%`}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="body1"
            component="p"
            aria-live="polite"
            sx={{
              fontWeight: 600,
              color: '#000000',
              fontSize: '16px',
              lineHeight: '24px',
              mb: detail ? 0.5 : 0,
            }}
          >
            {status}
          </Typography>
          {detail ? (
            <Typography
              variant="body2"
              component="p"
              sx={{
                color: 'text.secondary',
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              {detail}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BulkProgressModal;
